import React, { useState, useEffect } from 'react';
import { Spinner, Badge } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import InputMask from 'react-input-mask';
import axios from 'axios';
import StartSalesContractModal from './StartSalesContractModal';
import LeadCommissionChecklist from './LeadCommissionChecklist';
import CommercialAutomationStatusCard from './CommercialAutomationStatusCard';
import {
    commercialQualificationDetails,
    formatExtraValue,
} from './leadInformationDetails';

const LeadModal = ({ show, onHide, lead, onSave, onLeadConverted, colaboradores, pipelines = [], defaultPipelineId, currentUser, isSaving, showAlertModal, onOpenScheduleModal, onWhatsAppClick, currentEntidade }) => {
    const [formData, setFormData] = useState({});
    const [newNote, setNewNote] = useState('');
    const [tasks, setTasks] = useState([]);
    const [score, setScore] = useState(0);
    const [newTaskDescription, setNewTaskDescription] = useState('');
    const [newTaskDueDate, setNewTaskDueDate] = useState('');
    const [isConverting, setIsConverting] = useState(false);
    const [activeTab, setActiveTab] = useState('perfil');
    const [isVisible, setIsVisible] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [showSalesContract, setShowSalesContract] = useState(false);
    const [salesContractStatus, setSalesContractStatus] = useState(null);
    const [isCheckingSalesContract, setIsCheckingSalesContract] = useState(false);
    const [checkedSalesContractLeadId, setCheckedSalesContractLeadId] = useState(null);
    const [extraDraft, setExtraDraft] = useState({ key: '', value: '' });
    const [avatarImageFailed, setAvatarImageFailed] = useState(false);

    const isInstagramAtendimento = formData.connection?.channel === 'instagram'
        || (formData.channel_identities || []).some(identity => identity.channel === 'instagram');
    const atendimentoChannel = isInstagramAtendimento ? 'Instagram' : 'WhatsApp';
    const atendimentoIcon = isInstagramAtendimento ? faInstagram : faWhatsapp;
    const atendimentoColor = isInstagramAtendimento ? '#c13584' : '#25d366';
    const atendimentoGradient = isInstagramAtendimento
        ? 'linear-gradient(135deg, #833ab4 0%, #c13584 55%, #e1306c 100%)'
        : 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)';
    const activeModules = currentEntidade?.modulos_ativos;
    const hasClinicalModule = !Array.isArray(activeModules) || activeModules.includes('consultorio');
    const atendimentoIdentity = (formData.channel_identities || []).find(identity =>
        identity.channel === 'instagram'
        && (!formData.connection_id || Number(identity.connection_id) === Number(formData.connection_id))
    );
    const canOpenAtendimento = isInstagramAtendimento ? Boolean(atendimentoIdentity) : Boolean(formData.telefone);
    const atendimentoHistoricos = (formData.historicos || []).filter(history =>
        isInstagramAtendimento
            ? ['instagram', 'instagram_enviado'].includes(history.tipo_interacao)
            : ['whatsapp', 'whatsapp_enviado'].includes(history.tipo_interacao)
    );
    const getHistoryActorName = (item) => {
        const actor = item.dados_novos?.actor;
        if (actor?.type === 'automation') {
            return actor?.engine === 'commercial'
                ? 'Assistente Comercial'
                : 'Assistente';
        }
        const actorChannel = actor?.channel || (item.tipo_interacao === 'instagram' ? 'instagram' : null);
        const actorExternalUserId = actor?.external_user_id || item.dados_novos?.external_user_id;
        const identity = (formData.channel_identities || []).find(candidate =>
            candidate.channel === actorChannel
            && String(candidate.external_user_id) === String(actorExternalUserId)
        );

        return actor?.username
            || actor?.display_name
            || identity?.username
            || identity?.display_name
            || item.user?.nome
            || 'Sistema';
    };

    useEffect(() => {
        setAvatarImageFailed(false);
    }, [formData.foto_url]);

    useEffect(() => {
        if (show) {
            setTimeout(() => setIsVisible(true), 10);
            if (lead) {
                setFormData({ ...lead });
                setTasks(lead.tarefas || []);
                setScore(lead.pontuacao || 0);
            } else {
                const initialPipeline = pipelines.find(item => String(item.id) === String(defaultPipelineId)) || pipelines[0];
                const initialStage = initialPipeline?.estagios?.find(item => item.tipo === 'entrada') || initialPipeline?.estagios?.[0];
                setFormData({
                    nome: '', telefone: '', email: '', prioridade: 'baixa',
                    status: 'novo', historicos: [], origem_contato: null,
                    respondeu_contato: false, tarefas: [], responsavel_id: '',
                    pipeline_id: initialPipeline?.id || '', estagio_id: initialStage?.id || '', campos_extras: {},
                });
                setTasks([]);
                setScore(0);
            }
            setActiveTab(lead ? 'historico' : 'info');
            setNewNote('');
            setNewTaskDescription('');
            setNewTaskDueDate('');
        } else {
            setIsVisible(false);
        }
    }, [lead, show, pipelines, defaultPipelineId]);

    useEffect(() => {
        let mounted = true;
        const isCommercialLead = currentEntidade?.slug === 'fluxo-clinica'
            && lead?.id
            && lead?.campos_extras?.origem_cadastro === 'landing_fluxo_clinica';
        if (!show || !isCommercialLead) {
            setSalesContractStatus(null);
            setIsCheckingSalesContract(false);
            setCheckedSalesContractLeadId(null);
            return () => { mounted = false; };
        }

        setIsCheckingSalesContract(true);
        setCheckedSalesContractLeadId(null);
        axios.get(`/api/sales/leads/${lead.id}/commission-checklist`)
            .then(response => { if (mounted) setSalesContractStatus(response.data.contract); })
            .catch(() => { if (mounted) setSalesContractStatus({ lookup_failed: true }); })
            .finally(() => {
                if (mounted) {
                    setCheckedSalesContractLeadId(lead.id);
                    setIsCheckingSalesContract(false);
                }
            });
        return () => { mounted = false; };
    }, [show, lead?.id, lead?.campos_extras?.origem_cadastro, currentEntidade?.slug]);
    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => onHide(), 300);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePipelineChange = (event) => {
        const pipeline = pipelines.find(item => String(item.id) === event.target.value);
        const stage = pipeline?.estagios?.find(item => item.tipo === 'entrada') || pipeline?.estagios?.[0];
        setFormData(prev => ({ ...prev, pipeline_id: pipeline?.id || '', estagio_id: stage?.id || '', status: 'novo' }));
    };

    const updateExtraField = (key, value) => setFormData(prev => ({
        ...prev, campos_extras: { ...(prev.campos_extras || {}), [key]: value },
    }));

    const removeExtraField = (key) => setFormData(prev => {
        const extras = { ...(prev.campos_extras || {}) };
        delete extras[key];
        return { ...prev, campos_extras: extras };
    });

    const addExtraField = () => {
        const key = extraDraft.key.trim();
        const value = extraDraft.value.trim();
        if (!key || !value) {
            showAlertModal('Campo obrigatório', 'Informe o nome e o valor da nova informação.', 'warning');
            return;
        }
        const alreadyExists = Object.keys(formData.campos_extras || {})
            .some(existingKey => existingKey.toLocaleLowerCase('pt-BR') === key.toLocaleLowerCase('pt-BR'));
        if (alreadyExists) {
            showAlertModal('Campo já existente', 'Essa informação já foi cadastrada e permanece somente para leitura.', 'warning');
            return;
        }
        updateExtraField(key, value);
        setExtraDraft({ key: '', value: '' });
    };

    const handleAddTask = () => {
        if (!newTaskDescription || !newTaskDueDate) {
            showAlertModal('Campo Obrigatório', 'Preencha a descrição e a data da tarefa.', 'warning');
            return;
        }
        const newTask = {
            id: `temp-${Date.now()}`,
            descricao: newTaskDescription,
            data_vencimento: newTaskDueDate,
            concluida: 0,
            user_id: currentUser.id,
            user: { nome: currentUser.nome }
        };
        setTasks(prev => [...prev, newTask]);
        setNewTaskDescription('');
        setNewTaskDueDate('');
    };

    const handleToggleTask = (taskId) => {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, concluida: t.concluida ? 0 : 1 } : t));
    };

    const handleDeleteTask = (taskId) => {
        if (String(taskId).startsWith('temp-')) {
            setTasks(prev => prev.filter(t => t.id !== taskId));
        } else {
            setTasks(prev => prev.map(t => t.id === taskId ? { ...t, _delete: true } : t));
        }
    };

    const handleSave = () => {
        if (!lead && (!formData.pipeline_id || !formData.estagio_id)) {
            showAlertModal('Validação', 'Selecione a pipeline e a etapa inicial do lead.', 'warning');
            return;
        }
        if (formData.telefone && formData.telefone.includes('_')) {
            showAlertModal('Validação', 'Telefone incompleto.', 'warning');
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            showAlertModal('Validação', 'Email inválido.', 'warning');
            return;
        }
        onSave({ ...formData, nova_nota: newNote, pontuacao: score, tarefas: tasks });
    };

    const handleSchedule = async () => {
        if (!lead?.id) return;
        setIsConverting(true);
        try {
            const response = await axios.post(`/api/leads/${lead.id}/convert-to-patient`);
            if (response.data.conversion_destination === 'crm') {
                setFormData(previous => ({
                    ...previous,
                    status: 'convertido',
                    convertido: true,
                    convertido_em: response.data.lead?.convertido_em || previous.convertido_em,
                }));
                showAlertModal('Lead convertido', 'O lead foi convertido no CRM com sucesso.', 'success');
                onLeadConverted?.(response.data.lead);
                onHide();
                return;
            }

            onOpenScheduleModal(response.data.paciente);
            onHide();
        } catch (error) {
            if (error.response?.status === 409) {
                showAlertModal('Conflito de E-mail', error.response.data.message, 'warning');
            } else {
                showAlertModal('Erro', 'Não foi possível converter o lead.', 'danger');
            }
        } finally {
            setIsConverting(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            novo: '#3b82f6', contatado: '#8b5cf6', negociando: '#f97316',
            aguardando: '#eab308', convertido: '#22c55e', perdido: '#6b7280'
        };
        return colors[status] || '#6b7280';
    };

    const getStatusLabel = (status) => {
        const labels = {
            novo: 'Novo', contatado: 'Contatado', negociando: 'Negociando',
            aguardando: 'Aguardando', convertido: 'Convertido', perdido: 'Perdido'
        };
        return labels[status] || status;
    };

    const getPrioridadeColor = (p) => ({ alta: '#ef4444', media: '#f97316', baixa: '#22c55e' }[p] || '#6b7280');

    const avatarLetter = formData.nome?.charAt(0)?.toUpperCase() || '?';
    const statusColor = getStatusColor(formData.status);

    const kanbanSteps = [
        { id: 'novo', label: 'Novo' },
        { id: 'contatado', label: 'Contatado' },
        { id: 'negociando', label: 'Negociando' },
        { id: 'aguardando', label: 'Aguardando' },
        { id: 'convertido', label: 'Convertido' },
        { id: 'perdido', label: 'Perdido' },
    ];

    const currentStepIndex = kanbanSteps.findIndex(s => s.id === formData.status);
    const salesRequestData = formData.campos_extras || {};
    const isFluxoSalesLead = salesRequestData.origem_cadastro === 'landing_fluxo_clinica';
    const canStartSalesContract = currentEntidade?.slug === 'fluxo-clinica'
        && isFluxoSalesLead
        && lead?.id
        && formData.status !== 'convertido'
        && !isCheckingSalesContract
        && String(checkedSalesContractLeadId) === String(lead.id)
        && !salesContractStatus?.lookup_failed
        && salesContractStatus?.subscription_status !== 'active';
    const canUseCommissionChecklist = currentEntidade?.slug === 'fluxo-clinica' && isFluxoSalesLead && lead?.id;
    const salesRequestDetails = [
        { label: 'Clínica', value: formData.empresa },
        { label: 'Plano', value: salesRequestData.plano_interesse_label },
        { label: 'Interesse', value: salesRequestData.interesse_label },
        { label: 'Especialidade', value: salesRequestData.especialidade },
        { label: 'Profissionais', value: salesRequestData.quantidade_profissionais },
        { label: 'Necessidade', value: salesRequestData.principal_necessidade_label },
        {
            label: 'Localização',
            value: [formData.cidade, formData.estado].filter(Boolean).join(' / '),
        },
    ].filter(item => item.value);
    const extraFieldLabels = {
        interesse: 'Interesse', especialidade: 'Especialidade', referencia: 'Referência',
        pagina_origem: 'Página de origem', origem_cadastro: 'Origem do cadastro',
        plano_interesse: 'Plano', plano_interesse_label: 'Plano', interesse_label: 'Interesse',
        principal_necessidade: 'Necessidade', principal_necessidade_label: 'Necessidade',
        quantidade_profissionais: 'Profissionais', gclid: 'Google Click ID', fbclid: 'Facebook Click ID',
        tratamento_interesse:'Tratamento de interesse',
        unidade_interesse:'Unidade de interesse',
        preferencia_agendamento:'Preferência de agendamento',
        especialidade_agendamento:'Especialidade do agendamento',
        profissional_agendamento:'Profissional do agendamento',
        pendencia_agendamento:'Pendência do agendamento',
        objecoes_automacao:'Objeções identificadas',
        ultima_objecao_automacao:'Última objeção',
        ultima_mensagem_objecao:'Última mensagem da objeção',
        pendencia_automacao:'Motivo do atendimento humano',
    };
    const genericRequestDetails = [
        { label: formData.empresa ? 'Clínica' : 'Nome', value: formData.empresa || formData.nome },
        ...Object.entries(formData.campos_extras || {})
            .filter(([key]) => key !== 'commercial_qualification')
            .filter(([, value]) => value !== null && value !== '')
            .map(([key, value]) => ({
                label: extraFieldLabels[key] || key.replaceAll('_', ' ').replace(/^./, letter => letter.toUpperCase()),
                value: formatExtraValue(value),
            })),
        ...commercialQualificationDetails(
            salesRequestData.commercial_qualification,
            formData
        ),
        { label: 'Localização', value: [formData.cidade, formData.estado].filter(Boolean).join(' / ') },
    ].filter(item => item.value);
    const informationDetails = isFluxoSalesLead ? salesRequestDetails : genericRequestDetails;

    if (!show) return null;

    return (
        <>
            {/* Overlay */}
            <div
                onClick={handleClose}
                style={{
                    position: 'fixed', inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    zIndex: 1040,
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                }}
            />

            {/* Drawer */}
            <div className="lead-details-drawer" style={{
                position: 'fixed',
                top: 0, right: 0, bottom: 0,
                width: '90%', maxWidth: '1100px',
                backgroundColor: '#fff',
                zIndex: 1050,
                display: 'flex',
                transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s ease',
                boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
            }}>

                {/* Botão fechar flutuante */}
                <button
                    className="lead-details-close"
                    onClick={handleClose}
                    style={{
                        position: 'absolute', top: '24px', left: '-18px',
                        width: '36px', height: '36px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        zIndex: 10,
                    }}
                >
                    <FontAwesomeIcon icon="times" style={{ fontSize: `calc(14px * var(--font-scale))`, color: '#374151' }} />
                </button>

                {/* Overlay de salvamento */}
                {isSaving && (
                    <div style={{
                        position: 'absolute', inset: 0,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        zIndex: 20,
                    }}>
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p style={{ marginTop: '12px', color: '#6b7280', fontWeight: 500 }}>Salvando...</p>
                    </div>
                )}

                {/* ===== COLUNA ESQUERDA — Perfil ===== */}
                <div className="lead-details-profile" style={{
                    width: '300px', minWidth: '300px',
                    backgroundColor: '#f9fafb',
                    borderRight: '1px solid #e5e7eb',
                    display: 'flex', flexDirection: 'column',
                    overflowY: 'auto',
                }}>
                    {/* Header com gradiente */}
                    <div style={{
                        background: `radial-gradient(circle, ${statusColor}55 0%, ${statusColor}88 100%)`,
                        height: '80px', position: 'relative', flexShrink: 0,
                    }} />

                    {/* Avatar */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '-40px', padding: '0 16px 16px' }}>
                        <div style={{
                            width: '80px', height: '80px', borderRadius: '50%',
                            backgroundColor: `${statusColor}22`,
                            border: `3px solid #fff`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '28px', fontWeight: '300', color: statusColor,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            overflow: 'hidden',
                        }}>
                            {formData.foto_url && !avatarImageFailed ? (
                                <img
                                    src={formData.foto_url}
                                    alt={`Foto de ${formData.nome || 'lead'}`}
                                    onError={() => setAvatarImageFailed(true)}
                                    referrerPolicy="no-referrer"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                />
                            ) : avatarLetter}
                        </div>

                        {/* Nome editável */}
                        {editingField === 'nome' ? (
                            <input
                                autoFocus
                                value={formData.nome || ''}
                                name="nome"
                                onChange={handleChange}
                                onBlur={() => setEditingField(null)}
                                style={{
                                    marginTop: '8px', textAlign: 'center',
                                    fontSize: '16px', fontWeight: '600',
                                    border: 'none', borderBottom: '2px solid #3b82f6',
                                    background: 'transparent', outline: 'none',
                                    width: '100%',
                                }}
                            />
                        ) : (
                            <div
                                onClick={() => setEditingField('nome')}
                                style={{
                                    marginTop: '8px', fontSize: '16px', fontWeight: '600',
                                    color: '#111827', cursor: 'pointer', textAlign: 'center',
                                    padding: '2px 8px', borderRadius: '4px',
                                    maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}
                                title="Clique para editar"
                            >
                                {formData.nome || 'Sem nome'}
                            </div>
                        )}

                        {/* Status badge */}
                        <div style={{
                            marginTop: '6px', padding: '2px 10px',
                            borderRadius: '999px', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '500',
                            backgroundColor: `${statusColor}18`, color: statusColor,
                            border: `1px solid ${statusColor}44`,
                        }}>
                            {getStatusLabel(formData.status)}
                        </div>

                        {/* Pontuação */}
                        <div style={{ display: 'flex', gap: '4px', marginTop: '8px' }}>
                            {[1, 2, 3, 4, 5].map(star => (
                                <FontAwesomeIcon
                                    key={star}
                                    icon={star <= score ? 'star' : ['far', 'star']}
                                    onClick={() => setScore(star === score ? 0 : star)}
                                    style={{ cursor: 'pointer', color: '#f59e0b', fontSize: `calc(14px * var(--font-scale))` }}
                                />
                            ))}
                        </div>

                        {/* Responsável */}
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: `calc(12px * var(--font-scale))`, color: '#6b7280' }}>
                            <FontAwesomeIcon icon="user-circle" />
                            <select
                                name="responsavel_id"
                                value={formData.responsavel_id || ''}
                                onChange={handleChange}
                                style={{
                                    border: 'none', background: 'transparent',
                                    fontSize: `calc(12px * var(--font-scale))`, color: '#3b82f6', cursor: 'pointer',
                                    outline: 'none', textDecoration: 'underline',
                                }}
                            >
                                <option value="">Sem atendente</option>
                                {colaboradores.map(c => (
                                    <option key={c.id} value={c.id}>{c.nome}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '0 16px' }} />

                    {/* Notas rápidas */}
                    <div style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                            Notas
                        </div>
                        <textarea
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Adicionar anotação..."
                            rows={3}
                            style={{
                                width: '100%', padding: '8px 12px',
                                fontSize: `calc(13px * var(--font-scale))`, borderRadius: '6px',
                                border: '0 0 0 3px solid #3b82f6',
                                borderLeft: '3px solid #3b82f6',
                                backgroundColor: '#eff6ff',
                                outline: 'none', resize: 'none',
                                color: '#374151',
                            }}
                        />
                    </div>

                    <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '0 16px' }} />

                    {/* Abas do perfil */}
                    <div style={{ padding: '12px 16px', flex: 1 }}>
                        {/* Campos do perfil */}
                        {[
                            { label: 'Telefone', name: 'telefone', type: 'tel', mask: true },
                            { label: 'Email', name: 'email', type: 'email' },
                            { label: 'Prioridade', name: 'prioridade', type: 'select', options: ['baixa', 'media', 'alta'] },
                            { label: 'Origem', name: 'origem_contato', type: 'select', options: [null, 'clinica_iniciou', 'lead_iniciou', 'indicacao'], labels: ['Não especificado', 'Clínica iniciou', 'Lead iniciou', 'Indicação'] },
                        ].map(field => (
                            <div key={field.name} style={{ marginBottom: '12px' }}>
                                <label style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                                    {field.label}
                                </label>
                                {field.type === 'select' ? (
                                    <select
                                        name={field.name}
                                        value={formData[field.name] || ''}
                                        onChange={handleChange}
                                        style={{
                                            width: '100%', border: 'none',
                                            backgroundColor: 'transparent',
                                            fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280',
                                            cursor: 'pointer', outline: 'none',
                                            padding: '2px 0',
                                        }}
                                    >
                                        {field.options.map((opt, i) => (
                                            <option key={i} value={opt || ''}>{field.labels ? field.labels[i] : opt}</option>
                                        ))}
                                    </select>
                                ) : field.mask ? (
                                    <InputMask
                                        mask="(99) 99999-9999"
                                        value={formData[field.name] || ''}
                                        onChange={handleChange}
                                    >
                                        {(inputProps) => (
                                            <input
                                                {...inputProps}
                                                name={field.name}
                                                type={field.type}
                                                placeholder="(xx) xxxxx-xxxx"
                                                style={{
                                                    width: '100%', border: 'none',
                                                    backgroundColor: 'transparent',
                                                    fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280',
                                                    outline: 'none', padding: '2px 0',
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                ) : (
                                    <input
                                        name={field.name}
                                        type={field.type}
                                        value={formData[field.name] || ''}
                                        onChange={handleChange}
                                        placeholder={`Informe o ${field.label.toLowerCase()}`}
                                        style={{
                                            width: '100%', border: 'none',
                                            backgroundColor: 'transparent',
                                            fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280',
                                            outline: 'none', padding: '2px 0',
                                        }}
                                    />
                                )}
                                <div style={{ height: '1px', backgroundColor: '#f3f4f6', marginTop: '4px' }} />
                            </div>
                        ))}

                        {/* Respondeu contato */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                            <label style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '500', color: '#374151' }}>
                                Respondeu ao contato?
                            </label>
                            <div
                                onClick={() => setFormData(prev => ({ ...prev, respondeu_contato: !prev.respondeu_contato }))}
                                style={{
                                    width: '36px', height: '20px', borderRadius: '999px',
                                    backgroundColor: formData.respondeu_contato ? '#22c55e' : '#d1d5db',
                                    cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                                }}
                            >
                                <div style={{
                                    position: 'absolute', top: '2px',
                                    left: formData.respondeu_contato ? '18px' : '2px',
                                    width: '16px', height: '16px', borderRadius: '50%',
                                    backgroundColor: '#fff', transition: 'left 0.2s',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                }} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ===== COLUNA DIREITA — Conteúdo ===== */}
                <div className="lead-details-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                    {/* Pipeline de status */}
                    <div style={{
                        padding: '16px 24px 12px',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#fff',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', paddingBottom: '8px' }}>
                            {kanbanSteps.map((step, index) => {
                                const isActive = step.id === formData.status;
                                const isPast = index < currentStepIndex;
                                const color = getStatusColor(step.id);
                                return (
                                    <React.Fragment key={step.id}>
                                        <div
                                            onClick={() => setFormData(prev => ({ ...prev, status: step.id }))}
                                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', minWidth: '70px' }}
                                        >
                                            <div style={{
                                                width: '28px', height: '28px', borderRadius: '50%',
                                                backgroundColor: isActive ? color : isPast ? `${color}44` : '#f3f4f6',
                                                border: `2px solid ${isActive ? color : isPast ? color : '#d1d5db'}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'all 0.2s',
                                            }}>
                                                {isActive && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />}
                                                {isPast && <FontAwesomeIcon icon="check" style={{ fontSize: `calc(10px * var(--font-scale))`, color }} />}
                                            </div>
                                            <span style={{
                                                fontSize: `calc(10px * var(--font-scale))`, marginTop: '4px', textAlign: 'center',
                                                color: isActive ? color : '#9ca3af',
                                                fontWeight: isActive ? '600' : '400',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {step.label}
                                            </span>
                                        </div>
                                        {index < kanbanSteps.length - 1 && (
                                            <div style={{
                                                flex: 1, height: '2px', minWidth: '16px',
                                                backgroundColor: isPast ? getStatusColor(kanbanSteps[index].id) : '#e5e7eb',
                                                marginBottom: '18px', transition: 'background 0.2s',
                                            }} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    {/* Abas */}
                    <div style={{
                        display: 'flex', gap: '0', padding: '0 24px',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#fff',
                    }}>
                        {[
                            { key: 'historico', label: 'Histórico', icon: 'history' },
                            ...(canUseCommissionChecklist ? [{ key: 'comissao', label: 'Checklist', icon: 'tasks' }] : []),
                            { key: 'tarefas', label: 'Atividades', icon: 'tasks' },
                            { key: 'info', label: 'Informações', icon: 'info-circle' },
                            { key: 'atendimento', label: 'Atendimento', icon: atendimentoIcon },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                style={{
                                    padding: '12px 16px', border: 'none', background: 'none',
                                    cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                    color: activeTab === tab.key ? '#3b82f6' : '#6b7280',
                                    borderBottom: activeTab === tab.key ? '2px solid #3b82f6' : '2px solid transparent',
                                    marginBottom: '-1px', transition: 'all 0.15s',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                }}
                            >
                                <FontAwesomeIcon icon={tab.icon} style={{ fontSize: `calc(12px * var(--font-scale))` }} />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Conteúdo das abas */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>

                        {/* ABA: HISTÓRICO */}
                        {activeTab === 'historico' && (
                            <div>
                                {(formData.historicos || []).length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                                        <FontAwesomeIcon icon="history" style={{ fontSize: '32px', marginBottom: '8px', display: 'block' }} />
                                        Nenhum histórico ainda.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {(formData.historicos || []).map(item => (
                                            <div key={item.id} style={{
                                                backgroundColor: '#f9fafb',
                                                borderRadius: '8px', padding: '12px 16px',
                                                border: '1px solid #f3f4f6',
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                                    <span style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
                                                        {getHistoryActorName(item)}
                                                    </span>
                                                    <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                                                        {format(parseISO(item.created_at), "dd/MM/yyyy 'às' HH:mm")}
                                                    </span>
                                                </div>
                                                <p style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280', margin: 0 }}>{item.nota}</p>

                                                {item.dados_novos?.metadata?.media?.permalink && (
                                                    <a
                                                        href={item.dados_novos.metadata.media.permalink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ display: 'inline-block', marginTop: '6px', fontSize: `calc(12px * var(--font-scale))` }}
                                                    >
                                                        Ver publicacao comentada no Instagram
                                                    </a>
                                                )}

                                                {item.midia_url && item.midia_tipo === 'image' && (
                                                    <a href={item.midia_url} target="_blank" rel="noopener noreferrer">
                                                        <img src={item.midia_url} alt="Imagem" style={{ maxWidth: '200px', borderRadius: '6px', marginTop: '8px' }} />
                                                    </a>
                                                )}
                                                {item.midia_url && item.midia_tipo === 'video' && (
                                                    <video controls style={{ maxWidth: '100%', borderRadius: '6px', marginTop: '8px' }}>
                                                        <source src={item.midia_url} type="video/mp4" />
                                                    </video>
                                                )}
                                                {item.midia_url && item.midia_tipo === 'audio' && (
                                                    <audio controls style={{ width: '100%', marginTop: '8px' }}>
                                                        <source src={item.midia_url} type="audio/ogg" />
                                                    </audio>
                                                )}
                                                {item.midia_url && item.midia_tipo === 'sticker' && (
                                                    <img src={item.midia_url} alt="Sticker" style={{ maxWidth: '80px', marginTop: '8px' }} />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ABA: CHECKLIST DE COMISSÕES */}
                        {activeTab === 'comissao' && canUseCommissionChecklist && (
                            <LeadCommissionChecklist leadId={lead.id} showAlertModal={showAlertModal} />
                        )}

                        {/* ABA: TAREFAS */}
                        {activeTab === 'tarefas' && (
                            <div>
                                {/* Adicionar tarefa */}
                                <div style={{
                                    backgroundColor: '#f9fafb', borderRadius: '8px',
                                    padding: '12px', marginBottom: '16px',
                                    border: '1px solid #e5e7eb',
                                }}>
                                    <div style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                        Nova Atividade
                                    </div>
                                    <input
                                        value={newTaskDescription}
                                        onChange={(e) => setNewTaskDescription(e.target.value)}
                                        placeholder="Descreva a atividade..."
                                        style={{
                                            width: '100%', padding: '8px 12px',
                                            border: '1px solid #e5e7eb', borderRadius: '6px',
                                            fontSize: `calc(13px * var(--font-scale))`, marginBottom: '8px', outline: 'none',
                                        }}
                                    />
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            type="datetime-local"
                                            value={newTaskDueDate}
                                            onChange={(e) => setNewTaskDueDate(e.target.value)}
                                            style={{
                                                flex: 1, padding: '8px 12px',
                                                border: '1px solid #e5e7eb', borderRadius: '6px',
                                                fontSize: `calc(13px * var(--font-scale))`, outline: 'none',
                                            }}
                                        />
                                        <button
                                            onClick={handleAddTask}
                                            style={{
                                                padding: '8px 16px', backgroundColor: '#3b82f6',
                                                color: '#fff', border: 'none', borderRadius: '6px',
                                                fontSize: `calc(13px * var(--font-scale))`, cursor: 'pointer', fontWeight: '500',
                                            }}
                                        >
                                            + Adicionar
                                        </button>
                                    </div>
                                </div>

                                {/* Lista de tarefas */}
                                {tasks.filter(t => !t._delete).length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                                        <FontAwesomeIcon icon="tasks" style={{ fontSize: '32px', marginBottom: '8px', display: 'block' }} />
                                        Nenhuma atividade ainda.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {tasks.filter(t => !t._delete).map(task => (
                                            <div key={task.id} style={{
                                                display: 'flex', alignItems: 'center', gap: '12px',
                                                padding: '10px 14px', borderRadius: '8px',
                                                backgroundColor: task.concluida ? '#f9fafb' : '#fff',
                                                border: '1px solid #e5e7eb',
                                            }}>
                                                <div
                                                    onClick={() => handleToggleTask(task.id)}
                                                    style={{
                                                        width: '18px', height: '18px', borderRadius: '50%',
                                                        border: `2px solid ${task.concluida ? '#22c55e' : '#d1d5db'}`,
                                                        backgroundColor: task.concluida ? '#22c55e' : 'transparent',
                                                        cursor: 'pointer', flexShrink: 0,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}
                                                >
                                                    {task.concluida && <FontAwesomeIcon icon="check" style={{ fontSize: '9px', color: '#fff' }} />}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{
                                                        fontSize: `calc(13px * var(--font-scale))`, color: task.concluida ? '#9ca3af' : '#374151',
                                                        textDecoration: task.concluida ? 'line-through' : 'none',
                                                    }}>
                                                        {task.descricao}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '12px', marginTop: '2px' }}>
                                                        <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                                                            <FontAwesomeIcon icon="user" style={{ marginRight: '4px' }} />
                                                            {task.user?.nome || '...'}
                                                        </span>
                                                        <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                                                            <FontAwesomeIcon icon="calendar-alt" style={{ marginRight: '4px' }} />
                                                            {format(parseISO(task.data_vencimento), "dd/MM/yyyy HH:mm")}
                                                        </span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteTask(task.id)}
                                                    style={{
                                                        background: 'none', border: 'none',
                                                        color: '#d1d5db', cursor: 'pointer', padding: '4px',
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon="trash" style={{ fontSize: `calc(12px * var(--font-scale))` }} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ABA: INFORMAÇÕES */}
                        {activeTab === 'info' && (
                            <div>
                                {!lead && (
                                    <div style={{ backgroundColor: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #dbe3ed' }}>
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>Destino no Kanban</div>
                                        <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '12px' }}>Escolha em qual jornada e etapa este lead deve entrar.</p>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Pipeline
                                                <select value={formData.pipeline_id || ''} onChange={handlePipelineChange} style={{ width: '100%', marginTop: '5px', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff' }}>
                                                    <option value="">Selecione uma pipeline</option>
                                                    {pipelines.map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
                                                </select>
                                            </label>
                                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Etapa inicial
                                                <select value={formData.estagio_id || ''} onChange={event => setFormData(prev => ({ ...prev, estagio_id: event.target.value }))} disabled={!formData.pipeline_id} style={{ width: '100%', marginTop: '5px', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', background: '#fff' }}>
                                                    {(pipelines.find(item => String(item.id) === String(formData.pipeline_id))?.estagios || []).map(item => <option key={item.id} value={item.id}>{item.nome}</option>)}
                                                </select>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {!lead && (
                                <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' }}>
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>Informações personalizadas</div>
                                        <Badge bg="light" text="dark">{Object.keys(formData.campos_extras || {}).length} campos</Badge>
                                    </div>
                                    <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '12px' }}>Use estes campos para informações comerciais, especialidade, interesse, origem ou qualquer dado próprio da clínica.</p>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {Object.entries(formData.campos_extras || {}).map(([key, value]) => (
                                            <div key={key} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 32px', gap: '8px', alignItems: 'center' }}>
                                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis' }} title={key}>{key.replaceAll('_', ' ')}</div>
                                                <input value={value ?? ''} onChange={event => updateExtraField(key, event.target.value)} placeholder="Valor" style={{ padding: '9px 10px', border: '1px solid #dbe3ed', borderRadius: '8px', fontSize: '13px' }} />
                                                <button type="button" onClick={() => removeExtraField(key)} title="Remover campo" style={{ border: 0, background: '#fee2e2', color: '#b91c1c', borderRadius: '8px', height: '34px' }}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto', gap: '8px', marginTop: '12px' }}>
                                        <input list="lead-extra-field-suggestions" value={extraDraft.key} onChange={event => setExtraDraft({ ...extraDraft, key: event.target.value })} placeholder="Nome do campo" style={{ padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }} />
                                        <datalist id="lead-extra-field-suggestions">
                                            {['interesse','especialidade','referencia','pagina_origem','origem_cadastro','plano_interesse','principal_necessidade','quantidade_profissionais','gclid','fbclid'].map(item => <option key={item} value={item} />)}
                                        </datalist>
                                        <input value={extraDraft.value} onChange={event => setExtraDraft({ ...extraDraft, value: event.target.value })} placeholder="Valor" style={{ padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }} />
                                        <button type="button" onClick={addExtraField} style={{ padding: '9px 14px', border: 0, borderRadius: '8px', background: '#2563eb', color: '#fff', fontWeight: '600' }}>Adicionar</button>
                                    </div>
                                </div>
                                )}
                                {lead && informationDetails.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#eff6ff', borderRadius: '8px',
                                        padding: '16px', marginBottom: '16px',
                                        border: '1px solid #bfdbfe',
                                    }}>
                                        <div style={{
                                            fontSize: `calc(13px * var(--font-scale))`,
                                            fontWeight: '600', color: '#1e3a8a', marginBottom: '12px',
                                        }}>
                                            Dados da solicitação
                                        </div>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                            gap: '10px 16px',
                                        }}>
                                            {informationDetails.map(item => (
                                                <div key={item.label}>
                                                    <div style={{
                                                        fontSize: `calc(10px * var(--font-scale))`,
                                                        color: '#64748b', textTransform: 'uppercase',
                                                        letterSpacing: '0.04em',
                                                    }}>
                                                        {item.label}
                                                    </div>
                                                    <div style={{
                                                        marginTop: '2px',
                                                        fontSize: `calc(13px * var(--font-scale))`,
                                                        color: '#1e293b', fontWeight: '500',
                                                    }}>
                                                        {item.value}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {lead && (
                                    <div style={{
                                        backgroundColor: '#fff', borderRadius: '10px',
                                        padding: '14px 16px', marginBottom: '16px',
                                        border: '1px dashed #93c5fd',
                                    }}>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e3a8a', marginBottom: '3px' }}>
                                            Adicionar informação
                                        </div>
                                        <p style={{ margin: '0 0 10px', color: '#64748b', fontSize: '12px' }}>
                                            Inclua um novo interesse ou dado complementar. Informações já cadastradas permanecem somente para leitura.
                                        </p>
                                        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto', gap: '8px' }}>
                                            <input
                                                list="lead-extra-field-suggestions-existing"
                                                value={extraDraft.key}
                                                onChange={event => setExtraDraft({ ...extraDraft, key: event.target.value })}
                                                placeholder="Ex.: interesse"
                                                style={{ padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
                                            />
                                            <datalist id="lead-extra-field-suggestions-existing">
                                                {['interesse','especialidade','referencia','convenio','tratamento_interesse','principal_necessidade'].map(item => <option key={item} value={item} />)}
                                            </datalist>
                                            <input
                                                value={extraDraft.value}
                                                onChange={event => setExtraDraft({ ...extraDraft, value: event.target.value })}
                                                placeholder="Valor da informação"
                                                style={{ padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
                                            />
                                            <button
                                                type="button"
                                                onClick={addExtraField}
                                                style={{ padding: '9px 14px', border: 0, borderRadius: '8px', background: '#2563eb', color: '#fff', fontWeight: '600' }}
                                            >
                                                Incluir campo
                                            </button>
                                        </div>
                                        <small style={{ display: 'block', marginTop: '8px', color: '#64748b' }}>
                                            Depois de incluir, clique em Salvar para gravar a informação.
                                        </small>
                                    </div>
                                )}

                                {/* Pipeline visual */}
                                <div style={{
                                    backgroundColor: '#f9fafb', borderRadius: '8px',
                                    padding: '16px', marginBottom: '16px',
                                    border: '1px solid #e5e7eb',
                                }}>
                                    <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                        Jornada do Lead
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto' }}>
                                        {kanbanSteps.map((step, index) => {
                                            const isActive = step.id === formData.status;
                                            const isPast = index < currentStepIndex;
                                            const color = getStatusColor(step.id);
                                            return (
                                                <React.Fragment key={step.id}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '60px' }}>
                                                        <div style={{
                                                            width: '24px', height: '24px', borderRadius: '50%',
                                                            backgroundColor: isActive ? color : isPast ? `${color}44` : '#e5e7eb',
                                                            border: `2px solid ${isActive || isPast ? color : '#d1d5db'}`,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        }}>
                                                            {isPast && <FontAwesomeIcon icon="check" style={{ fontSize: '9px', color }} />}
                                                        </div>
                                                        <span style={{ fontSize: '9px', marginTop: '4px', color: isActive ? color : '#9ca3af', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                            {step.label}
                                                        </span>
                                                    </div>
                                                    {index < kanbanSteps.length - 1 && (
                                                        <div style={{ flex: 1, height: '2px', backgroundColor: isPast ? color : '#e5e7eb', minWidth: '12px', marginBottom: '14px' }} />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Métricas */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                                    {[
                                        { label: 'Prioridade', value: formData.prioridade, color: getPrioridadeColor(formData.prioridade), icon: 'flag' },
                                        { label: 'Pontuação', value: `${score}/5`, color: '#f59e0b', icon: 'star' },
                                        { label: 'Tarefas', value: `${tasks.filter(t => !t._delete && !t.concluida).length} pendentes`, color: '#3b82f6', icon: 'tasks' },
                                        { label: 'Histórico', value: `${(formData.historicos || []).length} registros`, color: '#8b5cf6', icon: 'history' },
                                    ].map(metric => (
                                        <div key={metric.label} style={{
                                            backgroundColor: '#fff', borderRadius: '8px',
                                            padding: '12px', border: '1px solid #e5e7eb',
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                        }}>
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '8px',
                                                backgroundColor: `${metric.color}18`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <FontAwesomeIcon icon={metric.icon} style={{ color: metric.color, fontSize: `calc(14px * var(--font-scale))` }} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>{metric.label}</div>
                                                <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151', textTransform: 'capitalize' }}>{metric.value}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ABA: ATENDIMENTO */}
                        {activeTab === 'atendimento' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                                <CommercialAutomationStatusCard
                                    leadId={lead?.id}
                                    colaboradores={colaboradores}
                                />

                                {/* Card principal do canal de atendimento */}
                                <div style={{
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    overflow: 'hidden',
                                }}>
                                    {/* Header do card */}
                                    <div style={{
                                        background: atendimentoGradient,
                                        padding: '20px 24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '44px', height: '44px', borderRadius: '50%',
                                                backgroundColor: 'rgba(255,255,255,0.2)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <FontAwesomeIcon icon={atendimentoIcon} style={{ fontSize: '22px', color: '#fff' }} />
                                            </div>
                                            <div>
                                                <div style={{ color: '#fff', fontWeight: '600', fontSize: `calc(15px * var(--font-scale))` }}>
                                                    {atendimentoChannel}
                                                </div>
                                                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: `calc(12px * var(--font-scale))` }}>
                                                    {isInstagramAtendimento
                                                        ? (atendimentoIdentity?.username ? `@${atendimentoIdentity.username}` : 'Sem identidade Instagram')
                                                        : (formData.telefone || 'Sem telefone cadastrado')}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Indicador de mensagens */}
                                        {atendimentoHistoricos.filter(h => !h.tipo_interacao.endsWith('_enviado')).length > 0 && (
                                            <div style={{
                                                backgroundColor: atendimentoColor,
                                                borderRadius: '999px',
                                                padding: '4px 10px',
                                                fontSize: `calc(12px * var(--font-scale))`,
                                                color: '#fff',
                                                fontWeight: '600',
                                            }}>
                                                {atendimentoHistoricos.filter(h => !h.tipo_interacao.endsWith('_enviado')).length} mensagens
                                            </div>
                                        )}
                                    </div>

                                    {/* Preview das últimas mensagens */}
                                    <div style={{ padding: '16px 24px' }}>
                                        {atendimentoHistoricos.length === 0 ? (
                                            <div style={{
                                                textAlign: 'center', padding: '24px',
                                                color: '#9ca3af', fontSize: `calc(13px * var(--font-scale))`,
                                            }}>
                                                <FontAwesomeIcon icon={atendimentoIcon} style={{ fontSize: '32px', color: '#d1d5db', display: 'block', margin: '0 auto 8px' }} />
                                                Nenhuma conversa ainda.<br />
                                                Inicie o atendimento pelo {atendimentoChannel}.
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                                                {atendimentoHistoricos
                                                    .slice(-5) // Últimas 5 mensagens
                                                    .map(item => {
                                                        const isEnviada = item.nota?.startsWith('📤');
                                                        return (
                                                            <div key={item.id} style={{
                                                                display: 'flex',
                                                                justifyContent: isEnviada ? 'flex-end' : 'flex-start',
                                                            }}>
                                                                <div style={{
                                                                    maxWidth: '80%',
                                                                    backgroundColor: isEnviada ? '#dcf8c6' : '#f3f4f6',
                                                                    borderRadius: isEnviada ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                                                    padding: '8px 12px',
                                                                    fontSize: `calc(12px * var(--font-scale))`,
                                                                    color: '#374151',
                                                                }}>
                                                                    <div style={{ wordBreak: 'break-word' }}>{item.nota}</div>
                                                                    {item.midia_url && item.midia_tipo === 'image' && (
                                                                        <img src={item.midia_url} alt="img" style={{ maxWidth: '100px', borderRadius: '4px', marginTop: '4px' }} />
                                                                    )}
                                                                    <div style={{ fontSize: `calc(10px * var(--font-scale))`, color: '#9ca3af', textAlign: 'right', marginTop: '2px' }}>
                                                                        {format(parseISO(item.created_at), "HH:mm")}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        )}
                                    </div>

                                    {/* Botão abrir chat */}
                                    <div style={{ padding: '0 24px 20px' }}>
                                        <button
                                            onClick={() => {
                                                if (onWhatsAppClick && lead) {
                                                    onWhatsAppClick(lead);
                                                    handleClose(); // Fecha o modal ao abrir o chat
                                                }
                                            }}
                                            disabled={!canOpenAtendimento}
                                            style={{
                                                width: '100%',
                                                padding: '12px',
                                                backgroundColor: canOpenAtendimento ? atendimentoColor : '#d1d5db',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '8px',
                                                cursor: canOpenAtendimento ? 'pointer' : 'not-allowed',
                                                fontSize: `calc(14px * var(--font-scale))`,
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                transition: 'background 0.2s',
                                            }}
                                            onMouseEnter={e => { if (canOpenAtendimento) e.currentTarget.style.filter = 'brightness(0.9)' }}
                                            onMouseLeave={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.backgroundColor = canOpenAtendimento ? atendimentoColor : '#d1d5db' }}
                                        >
                                            <FontAwesomeIcon icon={atendimentoIcon} style={{ fontSize: '18px' }} />
                                            {canOpenAtendimento
                                                ? `Abrir conversa no ${atendimentoChannel}`
                                                : (isInstagramAtendimento ? 'Identidade Instagram não encontrada' : 'Cadastre um telefone para iniciar')}
                                        </button>
                                    </div>
                                </div>

                                {/* Resumo do atendimento */}
                                <div style={{
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    padding: '16px 24px',
                                }}>
                                    <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                        Resumo do Atendimento
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                        {[
                                            {
                                                label: 'Mensagens recebidas',
                                                value: atendimentoHistoricos.filter(h => !h.tipo_interacao.endsWith('_enviado')).length,
                                                color: '#3b82f6',
                                                icon: 'comment',
                                            },
                                            {
                                                label: 'Mensagens enviadas',
                                                value: atendimentoHistoricos.filter(h => h.tipo_interacao.endsWith('_enviado')).length,
                                                color: '#22c55e',
                                                icon: 'paper-plane',
                                            },
                                            {
                                                label: 'Total de interações',
                                                value: atendimentoHistoricos.length,
                                                color: '#8b5cf6',
                                                icon: 'exchange-alt',
                                            },
                                        ].map(stat => (
                                            <div key={stat.label} style={{
                                                backgroundColor: `${stat.color}08`,
                                                borderRadius: '8px',
                                                padding: '12px',
                                                border: `1px solid ${stat.color}22`,
                                                textAlign: 'center',
                                            }}>
                                                <FontAwesomeIcon icon={stat.icon} style={{ color: stat.color, fontSize: '18px', marginBottom: '6px' }} />
                                                <div style={{ fontSize: '20px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                                                <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af', marginTop: '2px' }}>{stat.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer com ações */}
                    <div style={{
                        padding: '12px 24px',
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#fff',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        {canStartSalesContract && (
                            <button
                                type="button"
                                onClick={() => setShowSalesContract(true)}
                                disabled={isSaving}
                                style={{ padding: '8px 16px', borderRadius: '6px', backgroundColor: '#16a34a', color: '#fff', border: 'none', fontWeight: '600' }}
                            >
                                <FontAwesomeIcon icon="file-signature" /> Iniciar contratação
                            </button>
                        )}
                        {/* Botões de agendamento */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {lead && lead.status !== 'convertido' && (
                                <button
                                    onClick={handleSchedule}
                                    disabled={isSaving || isConverting}
                                    style={{
                                        padding: '8px 16px', borderRadius: '6px',
                                        backgroundColor: '#0ea5e9', color: '#fff',
                                        border: 'none', cursor: 'pointer',
                                        fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        opacity: isSaving || isConverting ? 0.7 : 1,
                                    }}
                                >
                                    {isConverting ? <Spinner as="span" size="sm" animation="border" /> : <FontAwesomeIcon icon="calendar-alt" />}
                                    {hasClinicalModule ? 'Agendar' : 'Converter lead'}
                                </button>
                            )}
                            {hasClinicalModule && lead && lead.status === 'convertido' && (
                                <button
                                    onClick={handleSchedule}
                                    disabled={isSaving || isConverting}
                                    style={{
                                        padding: '8px 16px', borderRadius: '6px',
                                        backgroundColor: '#f59e0b', color: '#fff',
                                        border: 'none', cursor: 'pointer',
                                        fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                    }}
                                >
                                    <FontAwesomeIcon icon="calendar-check" />
                                    Reagendar
                                </button>
                            )}
                        </div>

                        {/* Salvar / Cancelar */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={handleClose}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 16px', borderRadius: '6px',
                                    backgroundColor: '#f3f4f6', color: '#374151',
                                    border: '1px solid #e5e7eb', cursor: 'pointer',
                                    fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                style={{
                                    padding: '8px 20px', borderRadius: '6px',
                                    backgroundColor: '#3b82f6', color: '#fff',
                                    border: 'none', cursor: 'pointer',
                                    fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    opacity: isSaving ? 0.7 : 1,
                                }}
                            >
                                {isSaving ? <Spinner as="span" size="sm" animation="border" /> : <FontAwesomeIcon icon="save" />}
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <StartSalesContractModal
                show={showSalesContract}
                onHide={() => setShowSalesContract(false)}
                lead={lead}
                colaboradores={colaboradores}
                showAlertModal={showAlertModal}
            />        </>
    );
};

export default LeadModal;
